import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Check,
	ChevronDown,
	ChevronRight,
	Folder,
	Loader2,
	RotateCcw,
	Search,
	TriangleAlert,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";
import { ProgramColorDot } from "../programs/ProgramColorMark";
import { fetchAPI } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Program picker for the reference panel.
 *
 * Browsing mirrors the Programs section: coaches navigate folders (breadcrumb +
 * newest-created-first program list per folder). Typing switches to a flat search
 * across every program and folder, so a coach who remembers the name can grab it
 * without walking the tree.
 */

export const CURRENT_PROGRAM_SOURCE = "__current__";

export function ProgramSourcePicker({
	currentProgramId,
	currentProgramTitle,
	value,
	onChange,
	disabled = false,
}) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [programs, setPrograms] = useState([]);
	const [folders, setFolders] = useState([]);
	const [currentFolderId, setCurrentFolderId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [failed, setFailed] = useState(false);
	// A request must not be restarted by a re-render, and must survive the
	// popover closing mid-flight — hence refs rather than effect state.
	const requestStartedRef = useRef(false);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const loadPrograms = useCallback(async () => {
		requestStartedRef.current = true;
		setLoading(true);
		setFailed(false);

		// allSettled, not all: one scope failing (e.g. no shared programs access)
		// must not hide the other, but BOTH failing has to surface as an error
		// instead of an indistinguishable "no programs found".
		const [mine, shared, foldersMine, foldersShared] = await Promise.allSettled(
			[
				fetchAPI("/programs?scope=mine&compact=true"),
				fetchAPI("/programs?scope=shared&compact=true"),
				fetchAPI("/programs/folders/list?scope=mine"),
				fetchAPI("/programs/folders/list?scope=shared"),
			],
		);
		if (!mountedRef.current) return;

		if (mine.status === "rejected" && shared.status === "rejected") {
			setFailed(true);
			setLoading(false);
			requestStartedRef.current = false; // allow a retry
			return;
		}

		const byId = new Map();
		[...(mine.value || []), ...(shared.value || [])].forEach((item) => {
			if (item?.program_id && !byId.has(item.program_id)) {
				byId.set(item.program_id, item);
			}
		});
		setPrograms(Array.from(byId.values()));

		// Folders are best-effort: a failure just falls back to a flat list.
		const folderById = new Map();
		[
			...(foldersMine.status === "fulfilled" ? foldersMine.value || [] : []),
			...(foldersShared.status === "fulfilled"
				? foldersShared.value || []
				: []),
		].forEach((folder) => {
			if (folder?.folder_id && !folderById.has(folder.folder_id)) {
				folderById.set(folder.folder_id, folder);
			}
		});
		setFolders(Array.from(folderById.values()));

		setLoaded(true);
		setLoading(false);
	}, []);

	// Loaded lazily on first open: most sessions never leave the current program.
	useEffect(() => {
		if (!open || requestStartedRef.current) return;
		loadPrograms();
	}, [open, loadPrograms]);

	const isSearching = query.trim().length > 0;

	const folderById = useMemo(() => {
		const map = new Map();
		folders.forEach((folder) => {
			if (folder?.folder_id) map.set(folder.folder_id, folder);
		});
		return map;
	}, [folders]);

	// Breadcrumb from the current folder up to the root, so the coach can jump
	// back to any ancestor level.
	const breadcrumb = useMemo(() => {
		const path = [];
		let id = currentFolderId;
		const guard = new Set();
		while (id && folderById.has(id) && !guard.has(id)) {
			guard.add(id);
			const folder = folderById.get(id);
			path.unshift({ id: folder.folder_id, name: folder.name });
			id = folder.parent_id;
		}
		return path;
	}, [currentFolderId, folderById]);

	const getFolderPath = useCallback(
		(folderId) => {
			const parts = [];
			let id = folderId;
			const guard = new Set();
			while (id && folderById.has(id) && !guard.has(id)) {
				guard.add(id);
				const folder = folderById.get(id);
				parts.unshift(folder.name);
				id = folder.parent_id;
			}
			return parts.join(" / ");
		},
		[folderById],
	);

	// When searching we match folders by name too, so a coach can jump straight
	// into a matching folder instead of walking the tree.
	const visibleFolders = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		return folders
			.filter((folder) => {
				if (normalizedQuery) {
					return (folder.name || "").toLowerCase().includes(normalizedQuery);
				}
				return currentFolderId
					? folder.parent_id === currentFolderId
					: !folder.parent_id;
			})
			.sort((a, b) => {
				const aOrder = a?.sort_order ?? 0;
				const bOrder = b?.sort_order ?? 0;
				if (aOrder !== bOrder) return aOrder - bOrder;
				return (a.name || "").localeCompare(b.name || "");
			});
	}, [folders, query, currentFolderId]);

	const visiblePrograms = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		return programs
			.filter((item) => {
				if (item?.is_deleted) return false;
				// The program open in the editor is already offered as "This program",
				// which reads live state; picking it from the list would instead show a
				// fetched copy, i.e. the last saved version.
				if (currentProgramId && item?.program_id === currentProgramId) {
					return false;
				}
				if (normalizedQuery) {
					return (item.title || item.name || "")
						.toLowerCase()
						.includes(normalizedQuery);
				}
				// Browsing: only the programs filed in the current folder level.
				return currentFolderId
					? item.folder_id === currentFolderId
					: !item.folder_id;
			})
			// Newest created first, like the Programs section's default order:
			// updated_at moves whenever anything touches a program (even a sync of
			// a shared exercise), so it reshuffles the list for no visible reason.
			.sort((a, b) => {
				const aTime = Date.parse(a?.created_at || "") || 0;
				const bTime = Date.parse(b?.created_at || "") || 0;
				if (aTime !== bTime) return bTime - aTime;
				return (a.title || "").localeCompare(b.title || "");
			});
	}, [programs, query, currentProgramId, currentFolderId]);

	const openFolder = (folderId) => {
		setCurrentFolderId(folderId);
		setQuery("");
	};

	const selectedLabel =
		value === CURRENT_PROGRAM_SOURCE
			? t("pbSourceThisProgram")
			: programs.find((item) => item.program_id === value)?.title ||
				t("pbSourceOtherProgram");

	const select = (nextValue, nextTitle) => {
		onChange(nextValue, nextTitle);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					disabled={disabled}
					className="flex-1 min-w-0 inline-flex items-center gap-1.5 h-8 px-2 rounded-sm border border-border bg-secondary/40 text-xs text-foreground hover:border-volt/50 disabled:opacity-50"
				>
					<span className="truncate">{selectedLabel}</span>
					<ChevronDown className="w-3.5 h-3.5 ml-auto shrink-0 text-muted-foreground" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[320px] p-0 bg-card border-border"
				align="start"
			>
				<div className="p-2 border-b border-border">
					<div className="relative">
						<Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
						<Input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={t("pbSourceSearch")}
							className="bg-secondary border-transparent rounded-sm h-8 pl-7 text-xs"
						/>
					</div>
				</div>
				{!isSearching && breadcrumb.length > 0 && (
					<div className="flex items-center gap-1 px-2 py-1.5 border-b border-border text-[11px] text-muted-foreground flex-wrap">
						<button
							type="button"
							onClick={() => setCurrentFolderId(null)}
							className="hover:text-volt"
						>
							{t("pbSourceAllPrograms")}
						</button>
						{breadcrumb.map((node, index) => (
							<Fragment key={node.id}>
								<ChevronRight className="w-3 h-3 shrink-0" />
								{index === breadcrumb.length - 1 ? (
									<span className="text-foreground truncate">{node.name}</span>
								) : (
									<button
										type="button"
										onClick={() => setCurrentFolderId(node.id)}
										className="hover:text-volt truncate"
									>
										{node.name}
									</button>
								)}
							</Fragment>
						))}
					</div>
				)}
				<div className="max-h-72 overflow-y-auto p-1">
					{!isSearching && !currentFolderId && (
						<button
							type="button"
							onClick={() =>
								select(CURRENT_PROGRAM_SOURCE, currentProgramTitle || "")
							}
							className={`w-full text-left px-2 py-2 rounded-sm text-xs flex items-center gap-2 ${
								value === CURRENT_PROGRAM_SOURCE
									? "bg-volt/10 text-volt"
									: "hover:bg-secondary/60"
							}`}
						>
							<span className="truncate">{t("pbSourceThisProgram")}</span>
							{value === CURRENT_PROGRAM_SOURCE && (
								<Check className="w-3.5 h-3.5 ml-auto shrink-0" />
							)}
						</button>
					)}

					{loading && (
						<div className="flex items-center justify-center py-4 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
						</div>
					)}

					{!loading &&
						visibleFolders.map((folder) => (
							<button
								key={folder.folder_id}
								type="button"
								onClick={() => openFolder(folder.folder_id)}
								className="w-full text-left px-2 py-2 rounded-sm text-xs flex items-center gap-2 hover:bg-secondary/60"
							>
								<Folder className="w-3.5 h-3.5 shrink-0 text-volt" />
								<span className="truncate">{folder.name}</span>
								<ChevronRight className="w-3.5 h-3.5 ml-auto shrink-0 text-muted-foreground" />
							</button>
						))}

					{!loading &&
						visiblePrograms.map((item) => (
							<button
								key={item.program_id}
								type="button"
								onClick={() => select(item.program_id, item.title)}
								className={`w-full text-left px-2 py-2 rounded-sm text-xs flex items-center gap-2 ${
									value === item.program_id
										? "bg-volt/10 text-volt"
										: "hover:bg-secondary/60"
								}`}
							>
								<ProgramColorDot color={item.color} />
								<span className="min-w-0 flex-1">
									<span className="block truncate">
										{item.title || t("pbSourceUntitled")}
									</span>
									{isSearching && item.folder_id && (
										<span className="block truncate text-[10px] text-muted-foreground">
											{getFolderPath(item.folder_id)}
										</span>
									)}
								</span>
								{value === item.program_id && (
									<Check className="w-3.5 h-3.5 ml-auto shrink-0" />
								)}
							</button>
						))}

					{!loading && failed && (
						<div className="px-2 py-3 space-y-2">
							<p className="text-xs text-blaze flex items-center gap-1.5">
								<TriangleAlert className="w-3.5 h-3.5 shrink-0" />
								{t("pbSourceLoadFailed")}
							</p>
							<button
								type="button"
								onClick={loadPrograms}
								className="inline-flex items-center gap-1.5 h-7 px-2 rounded-sm border border-volt/50 text-volt text-xs hover:bg-volt/10"
							>
								<RotateCcw className="w-3.5 h-3.5" />
								{t("pbRetry")}
							</button>
						</div>
					)}

					{!loading &&
						!failed &&
						loaded &&
						visiblePrograms.length === 0 &&
						visibleFolders.length === 0 && (
							<p className="px-2 py-3 text-xs text-muted-foreground">
								{!isSearching && currentFolderId
									? t("pbSourceEmptyFolder")
									: t("pbSourceNoResults")}
							</p>
						)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
